import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form } from "react-bootstrap";
import { useCurrency } from "../../../../context/currencyContext";

/**
 * Props:
 *  - show: boolean
 *  - posting: the selected posting row (type: "Payment" | "Credit")
 *  - deleting: boolean (disable buttons while request is running)
 *  - onClose: () => void
 *  - onConfirm: (selectedInvoiceNumbers: string[]) => void
 */
const DeleteInvoicesModal = ({ show, posting, deleting, onClose, onConfirm }) => {
  const { formatAmount } = useCurrency();
  const [selected, setSelected] = useState([]);

  const applied = posting?.originalData?.appliedInvoices || [];

  // modal khulne par selection reset
  useEffect(() => {
    if (show) setSelected([]);
  }, [show, posting?.id]);

  const allSelected = applied.length > 0 && selected.length === applied.length;

  const toggleOne = (invoiceNumber) => {
    setSelected((prev) =>
      prev.includes(invoiceNumber)
        ? prev.filter((n) => n !== invoiceNumber)
        : [...prev, invoiceNumber]
    );
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : applied.map((a) => a.invoiceNumber));
  };

  const selectedTotal = applied
    .filter((a) => selected.includes(a.invoiceNumber))
    .reduce((sum, a) => sum + parseFloat(a.appliedAmount || 0), 0);

  if (!posting) return null;

  return (
    <Modal show={show} onHide={deleting ? undefined : onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton={!deleting} style={{
    backgroundColor: "#004aad",
    // color: "#ffffff",
    borderBottom: "none",
  }}>
        <Modal.Title style={{ fontSize: "1.15rem" }}>
          Delete {posting.type} – select invoices
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p style={{ color: "#555", marginBottom: "12px" }}>
          This {posting.type.toLowerCase()} is applied to {applied.length} invoices.
          Select the invoice(s) you want to remove. Unselected invoices will stay
          as they are.
        </p>

        <Table bordered hover size="sm" className="align-middle">
          <thead>
            <tr>
              <th style={{ width: "40px" }}>
                <Form.Check
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={deleting}
                />
              </th>
              <th>Invoice #</th>
              <th style={{ textAlign: "right" }}>Applied Amount</th>
            </tr>
          </thead>
          <tbody>
            {applied.map((a, idx) => (
              <tr
                key={`${a.invoiceNumber}-${idx}`}
                onClick={() => !deleting && toggleOne(a.invoiceNumber)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <Form.Check
                    type="checkbox"
                    checked={selected.includes(a.invoiceNumber)}
                    onChange={() => toggleOne(a.invoiceNumber)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={deleting}
                  />
                </td>
                <td>{String(a.invoiceNumber).padStart(6, "0")}</td>
                <td style={{ textAlign: "right" }}>
                  {formatAmount(parseFloat(a.appliedAmount || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div style={{ textAlign: "right", fontWeight: 600 }}>
          Selected: {selected.length} invoice(s) – {formatAmount(selectedTotal)}
        </div>

        {allSelected && (
          <div className="alert alert-warning mt-3 mb-0 py-2">
            All invoices are selected, so the whole {posting.type.toLowerCase()} will be deleted.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={selected.length === 0 || deleting}
          onClick={() => onConfirm(selected)}
        >
          {deleting ? "Deleting..." : `Delete Selected (${selected.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteInvoicesModal;